import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { type Product } from '@/data/allProducts';

// Safely parse a value that may be a JSONB object, a JSON string, or null
function safeParse<T>(value: unknown): T | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'object') return value as T; // already parsed (JSONB)
  if (typeof value === 'string') {
    try { return JSON.parse(value) as T; } catch { return undefined; }
  }
  return undefined;
}

export const useProducts = () => {
  const queryClient = useQueryClient();

  // Supabase realtime — invalidate cache whenever products table changes
  useEffect(() => {
    const channel = supabase
      .channel('products-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'products' }, () => {
        queryClient.invalidateQueries({ queryKey: ['products'] });
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [queryClient]);

  return useQuery({
    queryKey: ['products'],
    queryFn: async () => {
      // Queries products_public (a view that excludes cost_price and other
      // admin-only columns) rather than the raw products table — see the
      // migration this shipped alongside for why.
      const { data, error } = await supabase
        .from('products_public')
        .select('id,title,price,compare_at_price,image_url,images,category,description,short_description,slug,promo_label,promo_expires,key_features,tags,variants,inventory_count,sub_options,default_variant,status,created_at,title_ar,description_ar,seo_keywords_ar')
        .or('status.eq.active,status.is.null')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching products:', error);
        return [];
      }

      const dbProducts = (data || []).map((item: any): Product => ({
        id: item.id,
        title: item.title,
        price: item.price || 0,
        compareAtPrice: item.compare_at_price ?? undefined,
        image: item.image_url || (Array.isArray(item.images) && item.images[0]) || '',
        images: Array.isArray(item.images) && item.images.length > 0
          ? item.images
          : item.image_url ? [item.image_url] : [],
        category: item.category || 'Uncategorized',
        tag: item.category || 'General',
        rating: 5.0,
        reviews: Math.floor(Math.random() * 50) + 10,
        description: item.description ?? undefined,
        shortDescription: item.short_description ?? undefined,
        slug: item.slug ?? undefined,
        promoLabel: item.promo_label ?? undefined,
        promoExpires: item.promo_expires ?? undefined,
        keyFeatures: safeParse(item.key_features),
        tags: item.tags
          ? (typeof item.tags === 'string'
              ? item.tags.split(',').map((t: string) => t.trim()).filter(Boolean)
              : item.tags)
          : [],
        variants: safeParse(item.variants),
        inventoryCount: item.inventory_count ?? undefined,
        subOptions: safeParse(item.sub_options),
        defaultVariant: item.default_variant ?? undefined,
        titleAr: item.title_ar ?? undefined,
        descriptionAr: item.description_ar ?? undefined,
        seoKeywordsAr: item.seo_keywords_ar ?? undefined,
      }));

      return dbProducts;
    },
  });
};


